/* eslint-disable sonarjs/no-duplicate-string */
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import type { Prismic } from '@landsbankinn/prismic';
import { PrismicLink, WrapText } from '@landsbankinn/prismic';
import {
  device,
  Divider,
  typography,
  sizes,
  responseFont,
} from '@landsbankinn/ui';
import React from 'react';

import { animated } from 'react-spring';

interface InterestCardProps {
  title?: string;
  copy?: string;
  subTitle?: string;
  number?: React.ReactNode;
  color: string;
  link?: Prismic.Link;
  subTitleAlign: 'Left' | 'Top' | 'Bottom';
  style?: any;
}

function parent() {
  return css`
    display: flex;
    flex-flow: column nowrap;
    margin-bottom: 4em;

    @media ${device.tablet} {
      height: 100%;
      margin-bottom: 0;
    }
    @media ${device.desktop} {
      padding: 0 36px;
    }
  `;
}

const Host = styled(animated.div)`
  ${parent}
  position: relative;
`;

const Link = styled(animated.a)`
  ${parent}

  &:hover {
    h2 {
      opacity: 0.8;
    }
  }
`;

const Title = styled.h2`
  ${typography.h3}
  color: ${(props) => props.theme.colors.foreground};

  margin-bottom: calc(${25 / sizes.h3Size} * 1em);
`;

const Copy = styled.div`
  ${typography.body}
  color: ${(props) => props.theme.colors.foreground};

  @media ${device.tablet} {
    margin-bottom: 12px;
  }
`;

const Wrapper = styled.div<{ align: string }>`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  flex-direction: ${({ align }) =>
    align === 'Top' ? 'column' : align === 'Bottom' ? 'column-reverse' : 'row'};
  padding-top: 2em;

  @media ${device.tablet} {
    flex-direction: ${({ align }) =>
      align === 'Bottom' ? 'column-reverse' : 'column'};
    margin-top: ${({ align }) => (align === 'Left' ? 'auto' : '0')};
    padding-top: 20px;
    margin-bottom: -10px;
  }

  @media ${device.desktop} {
    flex-direction: ${({ align }) =>
      align === 'Top'
        ? 'column'
        : align === 'Bottom'
        ? 'column-reverse'
        : 'row'};
  }
`;

const SubTitle = styled.div<{ align: string }>`
  font-family: ${(props) => props.theme.font.familySerifCond};
  ${responseFont(18, 24)};
  margin-bottom: ${({ align }) => (align !== 'Top' ? '0' : '30px')};
  margin-top: ${({ align }) => (align === 'Bottom' ? '-10px' : '0')};
  margin-right: 15px;

  font-size: 24px;
  font-weight: 500;
  color: ${(props) => props.theme.colors.foreground};
`;

export const Number = styled.div<{ color: string }>`
  div {
    ${typography.numbersBiggest}
    font-weight: bold;
    color: ${({ color, theme }) =>
      color === 'Orange' ? theme.colors.secondary : theme.colors.foreground};
  }
`;

export const InterestCard = ({
  title,
  copy,
  subTitle,
  number,
  color,
  link,
  subTitleAlign,
  ...rest
}: InterestCardProps) => {
  const content = (
    <>
      <Title>{title}</Title>
      <Divider margin="15px 0 28px" />
      {!!copy && <Copy>{copy}</Copy>}
      {!!subTitle && !!number && (
        <Wrapper align={subTitleAlign}>
          <SubTitle align={subTitleAlign}>{subTitle}</SubTitle>
          <Number color={color}>{number}</Number>
        </Wrapper>
      )}
    </>
  );
  if (!link) {
    return <Host {...rest}>{content}</Host>;
  }
  return (
    <PrismicLink value={link} title={title}>
      <Link {...rest}>{content}</Link>
    </PrismicLink>
  );
};
